/**
 * Sets a cookie with the given name and value, and optional options.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {Object} [options] - Optional options for the cookie.
 * @param {number} [options.days] - The number of days until the cookie expires.
 * @param {string} [options.path] - The path of the cookie.
 * @param {string} [options.domain] - The domain of the cookie.
 * @param {boolean} [options.secure] - Whether the cookie should be secure.
 * @param {string} [options.sameSite] - The same site policy of the cookie.
 * @returns {void}
 */
export function setCookie(
  name: string,
  value: string,
  options?: {
    days?: number;
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: "Strict" | "Lax" | "None";
  }
): void {
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options?.days !== undefined) {
    const date = new Date();
    date.setTime(date.getTime() + options.days * 24 * 60 * 60 * 1000);
    cookie += `; expires=${date.toUTCString()}`;
  }

  cookie += `; path=${options?.path ?? "/"}`;

  if (options?.domain) {
    cookie += `; domain=${options.domain}`;
  }

  if (options?.secure) {
    cookie += "; secure";
  }

  if (options?.sameSite) {
    cookie += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookie;
}

/**
 * Retrieves a cookie with the given name.
 * @param {string} name The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie if found, otherwise null.
 */
export function getCookie(name: string): string | null {
  const encodedName = encodeURIComponent(name) + "=";
  const cookies = document.cookie.split("; ");

  for (const cookie of cookies) {
    if (cookie.startsWith(encodedName)) {
      return decodeURIComponent(cookie.slice(encodedName.length));
    }
  }

  return null;
}
